/**
 * The one Mixpanel wrapper for the web app.
 *
 * Nothing else imports mixpanel-browser (events.test.js enforces it). Every
 * event goes through track(), which only accepts names from events.js, drops
 * undefined properties, and in every non production build logs one console
 * line per call:
 *
 *   [analytics] <event_name> <JSON props>
 *
 * That line is what the click-through verification tails. Production never logs.
 *
 * Identity rules (tracking plan _meta.identity): distinct_id is the Firebase
 * uid. identify() only for real, non anonymous users; reset() on sign out.
 */
import mixpanel from 'mixpanel-browser';
import { EVENTS, EVENT_NAMES } from './events';

export * from './events';

// Public client tokens (the same ones index.js used to init with directly).
const PRODUCTION_TOKEN = '3811414af3c9cc4eb022fe637f2c7457';
const STAGING_TOKEN = '3cb407e740e118729cdb2a92eefd7982';

const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === 'production';
const KNOWN_EVENTS = new Set(EVENT_NAMES);

/**
 * Copy of `props` without undefined values, so optional properties that were
 * not available at the call site never reach Mixpanel as "undefined".
 * @param {Object | undefined} props
 * @returns {Object}
 */
function stripUndefined(props) {
  const clean = {};
  Object.keys(props || {}).forEach((key) => {
    if (props[key] !== undefined) clean[key] = props[key];
  });
  return clean;
}

/** @param {string} line */
function devLog(line) {
  if (IS_PRODUCTION) return;
  console.log(line);
}

/**
 * Init Mixpanel with the token for this environment and register the web
 * super properties. Called once from src/index.js before the app renders.
 * app_version is omitted on web: CRA exposes no build id without extra tooling.
 */
export function initAnalytics() {
  mixpanel.init(IS_PRODUCTION ? PRODUCTION_TOKEN : STAGING_TOKEN);
  registerSuperProperties({ platform: 'web' });
}

/**
 * Send one event. Unknown names are dropped (and shouted about outside
 * production) so a typo can never create a stray event in Mixpanel.
 * @template {import('./events').EventName} E
 * @param {E} event one of EVENTS
 * @param {import('./events').EventProps[E]} [props]
 */
export function track(event, props) {
  if (!KNOWN_EVENTS.has(event)) {
    if (!IS_PRODUCTION) {
      console.error(`[analytics] dropped unknown event "${event}". Add it to analytics/events.json first, then to src/analytics/events.js.`);
    }
    return;
  }
  const clean = stripUndefined(props);
  devLog(`[analytics] ${event} ${JSON.stringify(clean)}`);
  mixpanel.track(event, clean);
}

/**
 * Tie this browser's events to a real user. Refuses anonymous Firebase users:
 * their events stay on the device's anonymous distinct_id and merge into
 * whichever account they verify as later.
 * @param {{ uid: string, isAnonymous?: boolean } | null | undefined} user Firebase user
 */
export function identify(user) {
  if (!user || !user.uid || user.isAnonymous) return;
  devLog(`[analytics:identity] identify ${user.uid}`);
  mixpanel.identify(user.uid);
}

/**
 * Forget the current identity (sign out). A fresh anonymous distinct_id follows.
 * reset() also wipes super properties, so the static ones are registered again
 * (found in the staging walkthrough: events after a sign out lost `platform`).
 */
export function reset() {
  devLog('[analytics:identity] reset');
  mixpanel.reset();
  registerSuperProperties({ platform: 'web' });
}

/**
 * Register properties sent with every event from this browser.
 * @param {Object} props
 */
export function registerSuperProperties(props) {
  const clean = stripUndefined(props);
  devLog(`[analytics:super] register ${JSON.stringify(clean)}`);
  mixpanel.register(clean);
}

/** @param {Object} props */
export function setPeople(props) {
  const clean = stripUndefined(props);
  devLog(`[analytics:people] set ${JSON.stringify(clean)}`);
  mixpanel.people.set(clean);
}

/** @param {Object} props */
export function setPeopleOnce(props) {
  const clean = stripUndefined(props);
  devLog(`[analytics:people] set_once ${JSON.stringify(clean)}`);
  mixpanel.people.set_once(clean);
}

/**
 * @param {string} property
 * @param {number} [by]
 */
export function incrementPeople(property, by = 1) {
  devLog(`[analytics:people] increment ${property} ${by}`);
  mixpanel.people.increment(property, by);
}

/**
 * @param {string} property
 * @param {string | string[]} value
 */
export function unionPeople(property, value) {
  devLog(`[analytics:people] union ${property} ${JSON.stringify(value)}`);
  mixpanel.people.union(property, value);
}

/**
 * account_created plus the set-once signup_* profile properties, in one place
 * so both OTP paths (receipt OTPModal, groups JoinVerifyContainer) stamp the
 * same acquisition source. Call after identify().
 * @param {Object} params
 * @param {import('./events').Surface} params.surface
 * @param {string} [params.referrer_user_id] the host (receipt) or inviter (group)
 * @param {string} [params.receipt_id]
 * @param {string} [params.group_id]
 * @param {string} [params.invite_id]
 * @param {import('./events').LinkSource | string} params.source normalized link source at signup
 */
export function trackAccountCreated({ surface, referrer_user_id, receipt_id, group_id, invite_id, source }) {
  track(EVENTS.ACCOUNT_CREATED, {
    surface,
    invite_id,
    referrer_user_id,
    receipt_id,
    group_id,
  });
  setPeopleOnce({
    signup_platform: 'web',
    signup_surface: surface,
    signup_referrer_user_id: referrer_user_id,
    signup_receipt_id: receipt_id,
    signup_group_id: group_id,
    signup_invite_id: invite_id,
    signup_source: source,
    signup_at: new Date().toISOString(),
  });
}
